'use strict';

module.exports = /* @ngInject */ function (apiConfig, httpService, translationService) {
    const SEARCH_LIMIT = 50;

    function search(query = '', context = {}) {
        const uri = apiConfig.getBase('material-group', 'search');
        const searchQuery = (query || '').trim();
        const normalizedContext = normalizeContext(context);

        if (searchQuery) {
            uri.query.query = searchQuery;
        }
        uri.query.limit = SEARCH_LIMIT;
        applyContextQuery(uri, normalizedContext);

        return httpService.get(uri.toString(), {
            isArray: true,
            transformResponse: (response) => parseArray(response).map(mapMaterialGroup),
        });
    }

    function getRootGroups(context = {}) {
        const uri = apiConfig.getBase('material-group');
        const normalizedContext = normalizeContext(context);
        applyContextQuery(uri, normalizedContext);

        return httpService.get(uri.toString(), {
            isArray: true,
            transformResponse: (response) => parseArray(response).map(mapMaterialGroup),
        });
    }

    function getChildren(parentId, context = {}) {
        if (!parentId) {
            return Promise.resolve([]);
        }

        const uri = apiConfig.getBase('material-group', parentId, 'children');
        const normalizedContext = normalizeContext(context);
        applyContextQuery(uri, normalizedContext);

        return httpService.get(uri.toString(), {
            isArray: true,
            transformResponse: (response) => parseArray(response).map(mapMaterialGroup),
        });
    }

    function getById(materialGroupId, context = {}) {
        if (!materialGroupId) {
            return Promise.resolve(null);
        }
        const normalizedContext = normalizeContext(context);

        return getRootGroups(normalizedContext)
            .then((rootGroups) => findByIdInHierarchy(materialGroupId, rootGroups || [], new Set(), normalizedContext))
            .catch((error) => {
                console.error(error);
                return null;
            });
    }

    function getByCode(materialGroupCode, context = {}) {
        if (!materialGroupCode) {
            return Promise.resolve(null);
        }
        const normalizedContext = normalizeContext(context);

        return getRootGroups(normalizedContext)
            .then((rootGroups) => findByCodeInHierarchy(materialGroupCode, rootGroups || [], new Set(), normalizedContext))
            .catch((error) => {
                console.error(error);
                return null;
            });
    }

    function parseArray(response) {
        if (!response) {
            return [];
        }

        try {
            const parsed = typeof response === 'string' ? JSON.parse(response) : response;
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    function mapMaterialGroup(dto) {
        const code = dto?.code || '';
        const name = resolveLocalizedText(dto?.names);
        const description = resolveLocalizedText(dto?.descriptions);

        return {
            id: dto?.id || '',
            code,
            name,
            description,
            hasChildren: !!dto?.hasChildren,
            label: code && name ? `${code} - ${name}` : code || name || '',
        };
    }

    function resolveLocalizedText(values) {
        if (!Array.isArray(values) || values.length === 0) {
            return '';
        }

        const locale = normalizeLocale(translationService.getCurrentLocale());
        const language = locale.includes('_') ? locale.split('_')[0] : locale;
        const preferredLocales = [locale, language, 'de', 'en'].filter(Boolean);

        for (const preferredLocale of preferredLocales) {
            const match = values.find((entry) => normalizeLocale(entry?.language) === preferredLocale);
            if (match?.value) {
                return match.value;
            }
        }

        return values[0]?.value || '';
    }

    function normalizeLocale(locale) {
        return (locale || '').toString().trim().toLowerCase().replace('-', '_');
    }

    function findByIdInHierarchy(materialGroupId, groups, visitedParentIds, context) {
        const currentGroups = Array.isArray(groups) ? groups : [];
        const found = currentGroups.find((group) => group?.id === materialGroupId);
        if (found) {
            return Promise.resolve(found);
        }

        const parents = currentGroups.filter(
            (group) => group?.hasChildren && group?.id && !visitedParentIds.has(group.id),
        );
        if (parents.length === 0) {
            return Promise.resolve(null);
        }

        for (const parent of parents) {
            visitedParentIds.add(parent.id);
        }

        return Promise.all(
            parents.map((parent) =>
                getChildren(parent.id, context).catch((error) => {
                    console.error(error);
                    return [];
                }),
            ),
        ).then((childrenGroupArrays) => {
            const nextLevel = childrenGroupArrays.reduce((all, groupsOfParent) => all.concat(groupsOfParent), []);
            if (nextLevel.length === 0) {
                return null;
            }

            return findByIdInHierarchy(materialGroupId, nextLevel, visitedParentIds, context);
        });
    }

    function findByCodeInHierarchy(materialGroupCode, groups, visitedParentIds, context) {
        const currentGroups = Array.isArray(groups) ? groups : [];
        const found = currentGroups.find((group) => group?.code === materialGroupCode);
        if (found) {
            return Promise.resolve(found);
        }

        const parents = currentGroups.filter(
            (group) => group?.hasChildren && group?.id && !visitedParentIds.has(group.id),
        );
        if (parents.length === 0) {
            return Promise.resolve(null);
        }

        for (const parent of parents) {
            visitedParentIds.add(parent.id);
        }

        return Promise.all(
            parents.map((parent) =>
                getChildren(parent.id, context).catch((error) => {
                    console.error(error);
                    return [];
                }),
            ),
        ).then((childrenGroupArrays) => {
            const nextLevel = childrenGroupArrays.reduce((all, groupsOfParent) => all.concat(groupsOfParent), []);
            if (nextLevel.length === 0) {
                return null;
            }

            return findByCodeInHierarchy(materialGroupCode, nextLevel, visitedParentIds, context);
        });
    }

    function normalizeContext(context) {
        return {
            companyCodeId: normalizeValue(context?.companyCodeId),
            companyCode: normalizeValue(context?.companyCode),
            customerCompanyId: normalizeValue(context?.customerCompanyId),
        };
    }

    function normalizeValue(value) {
        if (typeof value === 'undefined' || value === null) {
            return '';
        }

        return String(value).trim();
    }

    function applyContextQuery(uri, context) {
        if (!uri || !context) {
            return;
        }

        if (context.companyCodeId) {
            uri.query.companyCodeId = context.companyCodeId;
            uri.query.bookingCircleId = context.companyCodeId;
        }

        if (context.companyCode) {
            uri.query.companyCode = context.companyCode;
            uri.query.bookingCircle = context.companyCode;
        }

        if (context.customerCompanyId) {
            uri.query.customerCompanyId = context.customerCompanyId;
        }
    }

    return {
        search,
        getRootGroups,
        getChildren,
        getById,
        getByCode,
    };
};
